import fixWebmDuration from 'fix-webm-duration';

export default class Recorder {
  private _audioDeviceId: string | null;
  public set audioDeviceId(deviceId: string | null) {
    this._audioDeviceId = deviceId;
  }
  public get audioDeviceId(): string | null {
    return this._audioDeviceId;
  }
  private _videoDeviceId: string | null;
  public set videoDeviceId(deviceId: string | null) {
    this._videoDeviceId = deviceId;
  }
  public get videoDeviceId(): string | null {
    return this._videoDeviceId;
  }

  private _blob: Blob | null;
  public get blob(): Blob | null {
    return this._blob;
  }

  private _stream: MediaStream | null;
  public get stream(): MediaStream | null {
    return this._stream;
  }

  private _isRecording: boolean;
  public get isRecording(): boolean {
    return this._isRecording;
  }

  private _chunks: BlobPart[];
  private _recorder: MediaRecorder | null;
  private _startTime: number | null;
  private _endTime: number | null;
  private _mimeType: string;
  onRecorderChange!: (isReady: boolean) => void;
  onStart!: (evt: Event) => void;
  onStop!: (evt: Event) => void;
  onComplete!: (blob: Blob) => void;
  onChunkAvailable!: (chunk: BlobEvent) => void;
  onError!: (err: Event) => void;

  constructor() {
    this._audioDeviceId = null;
    this._videoDeviceId = null;
    this._mimeType = 'video/mp4';
    this._blob = null;
    this._chunks = [];
    this._recorder = null;
    this._stream = null;
    this._startTime = null;
    this._endTime = null;
    this._isRecording = false;
  }

  dispose(): void {
    if (this._isRecording) {
      this.stopRecording();
    }

    if (this._stream) {
      this._stream.getTracks().forEach((t) => t.stop());
      this._stream = null;
    }
  }

  async initializeRecorder(): Promise<void> {
    this.onRecorderChange(false);
    if (!this._audioDeviceId) {
      throw Error('Cannot initialize recorder without specifying the audio device');
    }

    if (!this._videoDeviceId) {
      throw Error('Cannot initialize recorder without specifying the video device');
    }

    this.dispose();

    // All constraints: https://www.w3.org/TR/mediacapture-streams/#media-track-constraints
    // You can play around with constraints here: https://developer.mozilla.org/en-US/docs/Web/API/Media_Streams_API/Constraints#result
    this._stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: this._videoDeviceId, width: 1920, height: 1080 },
      audio: { deviceId: this._audioDeviceId }
    });

    // We default to video/mp4 as it plays nicely with the rest of Seismic, but if the browser doesn't support it we go back to webm (well supported in all browsers)
    if (!MediaRecorder.isTypeSupported(this._mimeType)) {
      this._mimeType = 'video/webm';
    }

    this._recorder = new MediaRecorder(this._stream, {
      mimeType: this._mimeType
    });
    this._initializeRecorder();
    this.onRecorderChange(true);
  }

  startRecording(): void {
    if (!this._recorder) {
      throw new Error('Recorder must be initialized with initializeRecorder');
    }

    this._isRecording = true;
    this._recorder.start();
  }

  stopRecording(): void {
    if (!this._recorder || !this._stream) {
      throw new Error('Recorder must be initialized with initializeRecorder');
    }

    this._recorder.stop();
    this._stream.getTracks().forEach((t) => t.stop());
    this._isRecording = false;
  }

  private _initializeRecorder(): void {
    if (!this._recorder) {
      throw new Error('Recorder must be initialized with initializeRecorder');
    }

    this._recorder.ondataavailable = (evt) => {
      if (this.onChunkAvailable) {
        this.onChunkAvailable(evt);
      }

      this._chunks.push(evt.data);
    };

    this._recorder.onerror = (evt) => {
      if (this.onError) {
        this.onError(evt);
      }
      this._blob = null;
      this._chunks = [];

      if (this._recorder) {
        this._recorder.stop();
        this._recorder = null;
      }

      if (this._stream) {
        this._stream.getTracks().forEach((t) => t.stop());
        this._stream = null;
      }
    };

    this._recorder.onpause = () => {
      throw new Error('Not Implemented');
    };

    this._recorder.onresume = () => {
      throw new Error('Not Implemented');
    };

    this._recorder.onstart = (evt) => {
      // For some reason, the onstart method is being fired after the user stops recording
      // This is causing problems downstream in our code. To handle this, we check if the recorder is
      // actually recording, and if not, we don't handle this event
      if (this._recorder?.state != 'recording') return;

      if (this.onStart) {
        this.onStart(evt);
      }
      this._startTime = Date.now();
      this._blob = null;
      this._chunks = [];
    };

    this._recorder.onstop = (evt) => {
      if (this.onStop) {
        this.onStop(evt);
      }
      this._endTime = Date.now();

      const badBlob = new Blob(this._chunks, { type: this._mimeType });

      if (this._startTime && this._endTime) {
        const totalTime = this._endTime - this._startTime;

        /**
         * Chrome has a bug where the Blob does not include duration - this breaks the preview (UP) and the uploaded asset doesn't have the correct metadata
         * https://bugs.chromium.org/p/chromium/issues/detail?id=642012
         * https://stackoverflow.com/questions/38443084/how-can-i-add-predefined-length-to-audio-recorded-from-mediarecorder-in-chrome/39971175#39971175
         * https://stackoverflow.com/questions/63640361/how-to-add-duration-to-metadata-of-files-recorder-by-mediarecorder
         * https://github.com/yusitnikov/fix-webm-duration
         */
        fixWebmDuration(badBlob, totalTime, (fixedBlob: Blob) => {
          this._blob = fixedBlob;

          if (this.onComplete) {
            this.onComplete(fixedBlob);
          }

          this._chunks = [];
        });
      } else {
        this._blob = badBlob;

        if (this.onComplete) {
          this.onComplete(badBlob);
        }

        this._chunks = [];
      }
    };
  }
}
